import type * as React from "react"
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom"
import {
  buildCanonicalBranchAdminUrl,
  buildOrganizationUrl,
  isParishBranchAlias,
} from "@/shared/lib/branch/branch-admin-routing"
import { attemptLazyImportRecovery, clearLazyImportRecovery } from "@/app/router/lazy-import-recovery"
import { ComingSoon } from "@/shared/components/coming-soon"
import { CREATE_PREFILL_STATE, createKindListTarget, type CreateKind, type CreatePrefill } from "@/features/create-overlay"

/**
 * Route guard for parish-level entries that are not built yet (Budgets, Audit Log). At parish level it renders
 * the shared Coming Soon placeholder; for a normal branch it renders the real feature via `<Outlet/>`. Keeps
 * branch behaviour untouched while giving the parish sidebar a consistent placeholder.
 */
export function ParishComingSoonRoute() {
  const { branchSlug } = useParams()
  if (isParishBranchAlias(branchSlug)) {
    return <ComingSoon />
  }
  return <Outlet />
}

export function lazyNamedComponent<TModule extends Record<string, unknown>, TKey extends keyof TModule>(
  importer: () => Promise<TModule>,
  exportName: TKey,
) {
  return async () => {
    try {
      const routeModule = await importer()
      const Component = routeModule[exportName]

      if (typeof Component !== "function") {
        throw new Error(`Route export "${String(exportName)}" is not a component`)
      }

      clearLazyImportRecovery()
      return { Component: Component as React.ComponentType }
    } catch (error) {
      if (attemptLazyImportRecovery(error)) {
        return new Promise<{ Component: React.ComponentType }>(() => {})
      }

      throw error
    }
  }
}

export function lazyDefaultComponent<TModule extends { default: React.ComponentType<unknown> }>(
  importer: () => Promise<TModule>,
) {
  return async () => {
    try {
      const routeModule = await importer()
      clearLazyImportRecovery()
      return { Component: routeModule.default }
    } catch (error) {
      if (attemptLazyImportRecovery(error)) {
        return new Promise<{ Component: React.ComponentType<unknown> }>(() => {})
      }

      throw error
    }
  }
}

/**
 * Resolves an in-app relative redirect (e.g. a retired feature path → its new home) to a flat tenant URL.
 * Uses the Organization form when there is no branch param (or the branch is the parish alias), otherwise the
 * branch form.
 */
function buildScopedAdminPath(params: Readonly<Record<string, string | undefined>>, suffix: string) {
  const workspaceSlug = params.workspaceSlug
  const branchSlug = params.branchSlug

  if (!workspaceSlug) {
    return "/dashboard"
  }

  if (!branchSlug || isParishBranchAlias(branchSlug)) {
    return buildOrganizationUrl(workspaceSlug, suffix)
  }

  return buildCanonicalBranchAdminUrl(workspaceSlug, branchSlug, suffix)
}

export function createAdminRedirectComponent(suffix: string) {
  return function AdminRedirectRoute() {
    const params = useParams()
    const location = useLocation()

    return <Navigate replace to={`${buildScopedAdminPath(params, suffix)}${location.search}`} />
  }
}

export function createAdminParamRedirectComponent(
  buildSuffix: (params: Readonly<Record<string, string | undefined>>) => string,
) {
  return function AdminParamRedirectRoute() {
    const params = useParams()
    const location = useLocation()

    return <Navigate replace to={`${buildScopedAdminPath(params, buildSuffix(params))}${location.search}`} />
  }
}

/**
 * An old create route (`/members/create`, `/ministers/create`): creating now opens over a page, so the route
 * replaces itself with the kind's list page with the overlay open (`?create=<kind>`). Bookmarks keep working and
 * leave no extra history entry. `resolvePrefill` carries what the old route read from its query (an activity id,
 * a saved calculation) into the form as its prefill, so old links still open the same starting point.
 */
export function createAdminCreateRedirectComponent(
  resolveKind: (search: URLSearchParams) => CreateKind,
  resolvePrefill?: (search: URLSearchParams) => CreatePrefill | null,
) {
  return function AdminCreateRedirectRoute() {
    const params = useParams()
    const location = useLocation()
    const search = new URLSearchParams(location.search)
    const kind = resolveKind(search)
    const prefill = resolvePrefill?.(search) ?? null

    return (
      <Navigate
        replace
        to={buildScopedAdminPath(params, createKindListTarget(kind))}
        state={prefill ? { [CREATE_PREFILL_STATE]: prefill } : undefined}
      />
    )
  }
}

export function createStaticRedirectComponent(to: string) {
  return function StaticRedirectRoute() {
    const location = useLocation()

    return <Navigate replace to={`${to}${location.search}`} />
  }
}

/**
 * `/finance/reconciliation/:sessionId` → its first tab.
 *
 * Relative so it works under both the organization root and a branch slug, and `replace` so the bare session
 * URL never becomes a back-button trap between the list and the tab.
 */
export function ReconciliationSessionIndexRedirect() {
  const location = useLocation()

  return <Navigate replace to={`transactions${location.search}`} />
}
